import React, { useState, useEffect } from 'react';
import { X, BadgeCheck, Mail, Info } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

/**
 * Samoobsługowe ustawienie własnego e-maila kadrowego (powiązanie konta z grafikiem).
 * Zapisuje wyłącznie pole kadryEmail zalogowanego użytkownika.
 */
const KadryEmailModal = ({ isOpen, onClose, currentKadryEmail, onSaved }) => {
  const [kadryEmail, setKadryEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { linked, message }

  useEffect(() => {
    if (isOpen) {
      setKadryEmail(currentKadryEmail || '');
      setResult(null);
    }
  }, [isOpen, currentKadryEmail]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = kadryEmail.trim();
    if (value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      toast.error('Podaj poprawny adres e-mail');
      return;
    }
    setLoading(true);
    try {
      const res = await api.put('/kadry/grafik/me/kadry-email', { kadryEmail: value });
      const data = res.data || {};
      setResult({ linked: !!data.linked, message: data.message || 'Zapisano.' });
      if (data.linked) {
        toast.success('Konto powiązane z kadrami');
      } else {
        toast(data.message || 'Zapisano', { icon: 'ℹ️' });
      }
      if (onSaved) await onSaved();
    } catch (err) {
      const msg = err.response?.data?.error || 'Nie udało się zapisać e-maila kadrowego';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            aria-label="Zamknij"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center">
              <BadgeCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">E-mail kadrowy</h2>
              <p className="text-xs text-gray-500">Powiązanie konta z grafikiem w kadrach</p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 mb-4 flex items-start gap-2 text-xs text-gray-600">
            <Info className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <span>
              Wpisz adres e-mail dokładnie taki, jaki masz zapisany w systemie kadrowym.
              Dzięki temu w zakładce <strong>Kadry → Mój grafik</strong> zobaczysz swój grafik i urlopy.
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="kadryEmailInput" className="block text-sm text-gray-700 mb-1.5">
                Adres e-mail w kadrach
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  id="kadryEmailInput"
                  type="email"
                  value={kadryEmail}
                  onChange={(e) => setKadryEmail(e.target.value)}
                  placeholder="np. jan.kowalski@przyklad.pl"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
              </div>
            </div>

            {result && (
              <div
                className={`rounded-lg px-3 py-2 text-sm ${
                  result.linked
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                    : 'bg-amber-50 text-amber-800 border border-amber-200'
                }`}
              >
                {result.message}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Zamknij
              </button>
              <button type="submit" disabled={loading} className="btn btn-primary flex items-center gap-2">
                {loading && <LoadingSpinner size="sm" />}
                Zapisz
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default KadryEmailModal;
